/**
 * CSV export service for MetricData Dev Tool
 * Handles CSV generation, filename creation, and download
 *
 * @module MetricDataDevTool/services/csvExportService
 */

import { format } from 'date-fns';
import { CSV_CONFIG } from '../constants';
import { getMetricDisplayName, formatMetricValue } from '../utils/metricDefinitions';

/**
 * Generate CSV filename based on filters
 * @param {Object} params - Export parameters
 * @param {string[]} params.selectedMetrics - Selected metrics
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {Object} params.aggregationConfig - Aggregation configuration
 * @returns {string} Generated filename
 */
export function generateCSVFilename(params) {
  const { selectedMetrics, startDate, endDate, aggregationConfig } = params;

  const parts = [CSV_CONFIG.FILENAME_PREFIX];

  // Add metric names (limit to 3 for filename brevity)
  if (selectedMetrics.length <= 3) {
    parts.push(selectedMetrics.join('_'));
  } else {
    parts.push(`${selectedMetrics.length}metrics`);
  }

  // Add date range
  parts.push(`${startDate}_to_${endDate}`);

  // Add aggregation info
  const aggParts = [];
  if (aggregationConfig.byDate) aggParts.push('byDate');
  if (aggregationConfig.byDayPart) aggParts.push('byDayPart');
  if (aggregationConfig.byDiningOption) aggParts.push('byDiningOption');

  if (aggParts.length > 0) {
    parts.push(aggParts.join('-'));
  }

  return `${parts.join('-')}.csv`;
}

/**
 * Convert array of objects to CSV string
 * @param {Array<Object>} data - Data to convert
 * @param {Array<string>} columns - Column headers
 * @returns {string} CSV string
 */
export function convertToCSV(data, columns) {
  if (!data || data.length === 0) {
    return '';
  }

  // Create header row
  const headers = columns.join(',');

  // Create data rows
  const rows = data.map(row => {
    return columns.map(col => {
      let value = row[col];

      // Format metric values
      if (col === 'numberValue' && row.metricName) {
        value = formatMetricValue(row.metricName, value);
      }

      // Handle null/undefined
      if (value === null || value === undefined) {
        value = '';
      }

      // Escape values containing commas or quotes
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    }).join(',');
  });

  return `${headers}\n${rows.join('\n')}`;
}

/**
 * Download CSV file
 * @param {string} csvContent - CSV content string
 * @param {string} filename - Filename for download
 */
export function downloadCSV(csvContent, filename) {
  // Add UTF-8 BOM for Excel compatibility
  const csvWithBOM = CSV_CONFIG.BOM + csvContent;

  // Create blob
  const blob = new Blob([csvWithBOM], {
    type: 'text/csv;charset=utf-8;'
  });

  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
}

/**
 * Get column names for CSV based on data structure
 * @param {Array<Object>} data - Data array
 * @param {Object} aggregationConfig - Aggregation configuration
 * @returns {Array<string>} Column names
 */
export function getCSVColumns(data, aggregationConfig) {
  if (!data || data.length === 0) {
    return [];
  }

  const columns = [];

  // Add dimension columns based on aggregation
  if (aggregationConfig.byDate && data[0].date !== undefined) {
    columns.push('date');
  }

  if (!aggregationConfig.byDate && !aggregationConfig.byDayPart && !aggregationConfig.byDiningOption) {
    // No aggregation - include all original columns
    if (data[0].metricName !== undefined) columns.push('metricName');
  }

  if (aggregationConfig.byDayPart && data[0].dayPart !== undefined) {
    columns.push('dayPart');
  }

  if (aggregationConfig.byDiningOption && data[0].diningOption !== undefined) {
    columns.push('diningOption');
  }

  // Add value column
  if (data[0].numberValue !== undefined) {
    columns.push('numberValue');
  }

  // Add record count for aggregated data
  if (data[0].recordCount !== undefined) {
    columns.push('recordCount');
  }

  // Add date for non-aggregated data
  if (!aggregationConfig.byDate && data[0].date !== undefined) {
    columns.push('date');
  }

  return columns;
}

/**
 * Export data to CSV
 * @param {Object} params - Export parameters
 * @param {Array<Object>} params.data - Data to export
 * @param {string[]} params.selectedMetrics - Selected metrics
 * @param {string} params.startDate - Start date
 * @param {string} params.endDate - End date
 * @param {Object} params.aggregationConfig - Aggregation configuration
 * @throws {Error} If data is empty or invalid
 */
export function exportToCSV(params) {
  const { data, selectedMetrics, startDate, endDate, aggregationConfig } = params;

  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  // Get columns
  const columns = getCSVColumns(data, aggregationConfig);

  // Convert to CSV
  const csvContent = convertToCSV(data, columns);

  // Generate filename
  const filename = generateCSVFilename({
    selectedMetrics,
    startDate,
    endDate,
    aggregationConfig
  });

  // Download
  downloadCSV(csvContent, filename);
}
